import type { PathOptions, CircleMarkerOptions } from "leaflet";

/**
 * Shared map style constants
 * All map components should use these as a base to ensure consistency
 */

// ============================================================================
// BASE GEOMETRY STYLES
// ============================================================================

/** Base radius for Point geometry markers */
export const POINT_MARKER_RADIUS = 12;

/** Border weight for Point geometry markers */
export const POINT_MARKER_WEIGHT = 2;

/** Border weight for Polygon geometry */
export const POLYGON_BORDER_WEIGHT = 1.5;

// ============================================================================
// COLOR PALETTE
// ============================================================================

/** Primary blue color for active/selected features */
export const COLOR_PRIMARY = "rgba(59, 130, 246, 1)";

/** Neutral gray for borders - increased opacity for better visibility */
export const COLOR_BORDER_NEUTRAL = "rgba(71, 85, 105, 0.7)";

/** White for point marker borders */
export const COLOR_BORDER_WHITE = "rgba(255, 255, 255, 0.9)";

/** Dimmed gray for disallowed features */
export const COLOR_DISALLOWED = "rgba(51, 65, 85, 0.2)";
export const COLOR_DISALLOWED_FILL = "#e2e8f0";

/** Red for excluded/error features */
export const COLOR_EXCLUDED = "rgba(239, 68, 68, 0.4)";
export const COLOR_EXCLUDED_FILL = "rgba(239, 68, 68, 0.15)";

// ============================================================================
// LEAFLET PATH OPTIONS
// ============================================================================

/**
 * Default style for Point geometries
 * Used by BaseMap when rendering points
 */
export const DEFAULT_POINT_STYLE: CircleMarkerOptions = {
  radius: POINT_MARKER_RADIUS,
  fillColor: COLOR_PRIMARY,
  fillOpacity: 0.7,
  color: COLOR_BORDER_WHITE,
  weight: POINT_MARKER_WEIGHT,
  opacity: 1,
};

/**
 * Default style for Polygon/LineString geometries
 * Used for preview and general display
 */
export const DEFAULT_POLYGON_STYLE: PathOptions = {
  weight: 1,
  color: COLOR_BORDER_NEUTRAL,
  fillColor: "rgba(59, 130, 246, 0.25)",
  fillOpacity: 0.3,
  opacity: 0.7,
};

/**
 * Style for features that are not allowed/clickable
 * (e.g., filtered out features in QuizStudio)
 */
export const DISALLOWED_FEATURE_STYLE: PathOptions = {
  weight: 0.8,
  color: COLOR_DISALLOWED,
  fillColor: COLOR_DISALLOWED_FILL,
  fillOpacity: 0.15,
  opacity: 0.4,
};

/**
 * Style for excluded features
 * (e.g., features removed from a quiz)
 */
export const EXCLUDED_FEATURE_STYLE: PathOptions = {
  weight: 1,
  color: COLOR_EXCLUDED,
  fillColor: COLOR_EXCLUDED_FILL,
  fillOpacity: 0.2,
  opacity: 0.5,
};

/**
 * Style applied on hover (merged with base style)
 */
export const HOVER_STYLE: PathOptions = {
  weight: 2,
  fillOpacity: 0.45,
  opacity: 1,
};

// ============================================================================
// MAP STYLE TYPES
// ============================================================================

export type MapStyle = "light" | "dark" | "voyager" | "satellite" | "osm";

// ============================================================================
// FEATURE STATE SYSTEM
// ============================================================================

/**
 * Semantic states that features can be in.
 * BaseMap uses this to apply appropriate styling.
 */
export type FeatureState =
  | "default"      // Normal, interactive feature
  | "correct"      // Correctly answered in quiz
  | "incorrect"    // Incorrectly answered in quiz
  | "pending"      // Not yet answered in quiz
  | "mixed"        // Partially correct (for grouped features)
  | "disabled"     // Not clickable/interactable
  | "excluded"     // Explicitly excluded from quiz
  | "hover";       // Currently being hovered

/**
 * Progress information for a feature (used in quiz context)
 */
export interface FeatureProgress {
  total: number;
  correct: number;
  incorrect: number;
}

/**
 * Get the appropriate style for a feature based on its state and progress
 */
export function getFeatureStateStyle(
  state: FeatureState,
  progress?: FeatureProgress,
  isPoint: boolean = false
): PathOptions {
  const baseStyle = isPoint ? DEFAULT_POINT_STYLE : DEFAULT_POLYGON_STYLE;

  switch (state) {
    case "correct":
      return {
        ...baseStyle,
        fillColor: "rgba(34, 197, 94, 1)", // Green-500
        fillOpacity: isPoint ? 0.7 : 0.42,
        color: isPoint ? COLOR_BORDER_WHITE : "rgba(34, 197, 94, 0.8)",
        weight: isPoint ? POINT_MARKER_WEIGHT : POLYGON_BORDER_WEIGHT,
        opacity: 0.9,
      };

    case "incorrect":
      return {
        ...baseStyle,
        fillColor: "rgba(239, 68, 68, 1)", // Red-500
        fillOpacity: isPoint ? 0.7 : 0.42,
        color: isPoint ? COLOR_BORDER_WHITE : "rgba(239, 68, 68, 0.8)",
        weight: isPoint ? POINT_MARKER_WEIGHT : POLYGON_BORDER_WEIGHT,
        opacity: 0.9,
      };

    case "pending":
      return {
        ...baseStyle,
        fillColor: COLOR_PRIMARY,
        fillOpacity: isPoint ? 0.7 : 0.28,
        color: isPoint ? COLOR_BORDER_WHITE : COLOR_BORDER_NEUTRAL,
        weight: isPoint ? POINT_MARKER_WEIGHT : POLYGON_BORDER_WEIGHT,
        opacity: isPoint ? 1 : 0.8,
      };

    case "mixed":
      // Blend colors based on progress
      if (progress) {
        const totalAnswered = progress.correct + progress.incorrect;
        const pending = Math.max(progress.total - totalAnswered, 0);

        // Weighted RGB blend
        const r = Math.round((34 * progress.correct + 239 * progress.incorrect + 59 * pending) / progress.total);
        const g = Math.round((197 * progress.correct + 68 * progress.incorrect + 130 * pending) / progress.total);
        const b = Math.round((94 * progress.correct + 68 * progress.incorrect + 246 * pending) / progress.total);

        return {
          ...baseStyle,
          fillColor: `rgb(${r}, ${g}, ${b})`,
          fillOpacity: isPoint ? 0.7 : 0.42,
          color: isPoint ? COLOR_BORDER_WHITE : `rgba(${r}, ${g}, ${b}, 0.8)`,
          weight: isPoint ? POINT_MARKER_WEIGHT : POLYGON_BORDER_WEIGHT,
          opacity: 0.9,
        };
      }
      return getFeatureStateStyle("pending", undefined, isPoint);

    case "disabled":
      return DISALLOWED_FEATURE_STYLE;

    case "excluded":
      return EXCLUDED_FEATURE_STYLE;

    case "hover":
      // Hover is applied as a modifier, not a base state
      return HOVER_STYLE;

    case "default":
    default:
      return baseStyle;
  }
}

/**
 * Apply hover effect to a base style
 */
export function applyHoverEffect(baseStyle: PathOptions): PathOptions {
  return {
    ...baseStyle,
    ...HOVER_STYLE,
    fillOpacity: Math.min((baseStyle.fillOpacity ?? 0.3) + 0.14, 1),
  };
}
